import React from "react";
import { View } from "react-native";
import dayjs from "dayjs";
import {
  Panel,
  Text,
  theme,
  Pill,
  Heading,
} from "@clearabee/ui-library-native";
import { Anchor } from "components/Core/Anchor/Anchor";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { useCatalogue } from "@clearabee/ui-sdk";
import { useTranslation } from "react-i18next";
import { styles } from "./JobsView.styles";
import {
  convertApiStatus,
  formatPostcode,
  getTopCategoryFromItems,
} from "lib/utils";
import { JobsViewProps } from "./JobsView.types";
import { STATUS_COLORS } from "lib/constants/statusColors";

export const SmallScreenView = ({
  skeletonSize,
  jobs,
}: JobsViewProps): React.ReactElement => {
  const [translate] = useTranslation();
  const catalogue = useCatalogue();

  return (
    <Panel
      styles={{ padding: 0, marginBottom: theme.spacing.large }}
      shadow={false}
    >
      {jobs?.map((job, index) => (
        <Anchor
          key={`${job.ref}-${index}`}
          routeName="Job"
          params={{ ref: job.ref }}
        >
          <View
            style={{
              padding: theme.spacing.medium,
              height: 108, // for consistent cell height
            }}
          >
            <View
              style={{
                position: "absolute",
                top: theme.spacing.xsmall,
                right: theme.spacing.medium,
                alignItems: "flex-end",
              }}
            >
              <Pill
                size="small"
                color={STATUS_COLORS[job.status as keyof typeof STATUS_COLORS]}
                styles={{
                  minWidth: theme.spacing.xlarge4,
                  alignItems: "center",
                  marginTop: theme.spacing.xsmall,
                }}
              >
                {convertApiStatus(job.status) ?? job.status}
              </Pill>
              <Text
                fontSize="small"
                styles={{
                  marginTop: theme.spacing.xsmall2,
                }}
              >
                {job.ref}
              </Text>
              <Text
                fontSize="small"
                styles={{
                  fontFamily: "ProximaBold",
                }}
              >
                {formatPostcode(job.addressPostcode)}
              </Text>
            </View>
            <Heading
              fontSize="xsmall2"
              styles={{
                textTransform: "uppercase",
              }}
            >
              {getTopCategoryFromItems(
                catalogue.defaultCatalogue,
                job?.items ?? [],
              )?.title ?? " "}
            </Heading>
            <Text
              color="brand"
              fontSize="base"
              styles={{
                fontFamily: "ProximaBold",
                marginTop: theme.spacing.xsmall2,
              }}
            >
              {dayjs(job.date).format("ddd, Do MMM YYYY")}
            </Text>
            <Heading fontSize="small">
              {job.timePreference === "Any" &&
                translate(
                  "hwa.screens.dashboard.userDashboard.timeslotPlaceholder",
                )}
              {job.timePreference !== "Any" &&
                job.timePreference !== "" &&
                job.timePreference}
              {job.timeslotJson &&
                `${dayjs(`2020-01-01T${job.timeslotJson.from}`).format(
                  "ha",
                )} - ${dayjs(`2020-01-01T${job.timeslotJson.to}`).format(
                  "ha",
                )}`}
            </Heading>
          </View>
          {index !== jobs.length - 1 && (
            <View
              style={{
                marginHorizontal: theme.spacing.medium,
                borderBottomColor: theme.colors.greyscale.lightest,
                borderBottomWidth: 1,
              }}
            />
          )}
        </Anchor>
      ))}
      {jobs?.length === 0 && (
        <Text styles={styles.noPreviousOrders}>
          {translate("hwa.components.jobsView.noPreviousOrders")}
        </Text>
      )}
      {!jobs &&
        Array.from(Array(skeletonSize).keys()).map((index) => (
          <SkeletonLoader
            key={index}
            speed={1}
            backgroundColor={theme.colors.light.base}
            foregroundColor={`${theme.colors.greyscale.lighter}00`}
            height={75}
            width="100%"
          >
            <Rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </SkeletonLoader>
        ))}
    </Panel>
  );
};
